/**
 * Validates project status workflow transitions
 * This is a frontend version of the backend validateWorkflowTransition function
 * to ensure consistent validation across both frontend and backend
 */

/**
 * Validates if a status transition is allowed according to business rules
 * @param currentStatus The current status of the project
 * @param newStatus The requested new status
 * @param isAdmin Whether the user is an admin or super admin (no longer bypasses validation rules)
 * @returns Object with validation result and error message if applicable
 */
export const validateWorkflowTransition = (
  currentStatus: string | null | undefined,
  newStatus: string,
  isAdmin: boolean = false
): {
  valid: boolean;
  message?: string;
} => {
  // Handle missing or empty statuses
  if (!currentStatus) {
    // If there's no current status, any status is valid for initial assignment
    return { valid: true };
  }

  // Normalize statuses to uppercase
  const current = currentStatus.toUpperCase();
  const next = newStatus.toUpperCase();

  // If status isn't changing, no validation needed
  if (current === next) {
    return { valid: true };
  }

  // Rule 1: CANCELLED projects can't be changed except by admins
  if (current === 'CANCELLED' && !isAdmin) {
    return {
      valid: false,
      message: 'Cannot change status of a CANCELLED project (only admins can reopen)',
    };
  }

  // Rule 2: BILLED status can only come from DELIVERED
  if (next === 'BILLED' && current !== 'DELIVERED') {
    return {
      valid: false,
      message: 'Project must be DELIVERED before it can be BILLED',
    };
  }

  // Rule 3: HOLD state rules
  if (next === 'HOLD') {
    // Projects in Stage 2,3,4 can go to HOLD
    const validHoldOrigins = ['ASSIGNED', 'IN_PROGRESS', 'REVIEW'];
    if (!validHoldOrigins.includes(current)) {
      return {
        valid: false,
        message: 'Project can only be moved to HOLD from ASSIGNED, IN_PROGRESS, or REVIEW status',
      };
    }
  }

  // Rule 4: Transitions from HOLD
  if (current === 'HOLD') {
    // Projects in HOLD can go to stages 2,3,4,5,6
    const validHoldDestinations = ['ASSIGNED', 'IN_PROGRESS', 'REVIEW', 'DELIVERED', 'BILLED'];
    if (!validHoldDestinations.includes(next)) {
      return {
        valid: false,
        message: 'Projects on HOLD can only move to ASSIGNED, IN_PROGRESS, REVIEW, DELIVERED, or BILLED status',
      };
    }
  }

  // Rule 5: CANCELLED state rules
  if (next === 'CANCELLED') {
    // Projects in Stage 2,3,4,5,7 can go to CANCELLED
    const validCancelOrigins = ['ASSIGNED', 'IN_PROGRESS', 'REVIEW', 'DELIVERED', 'HOLD'];
    if (!validCancelOrigins.includes(current)) {
      return {
        valid: false,
        message: 'Project can only be CANCELLED from ASSIGNED, IN_PROGRESS, REVIEW, DELIVERED or HOLD status',
      };
    }
  }

  // Rule 6: REOPEN state rules
  if (next === 'REOPEN') {
    // Projects in Stage 5,6,9 can be reopened
    const validReopenOrigins = ['DELIVERED', 'BILLED', 'PAYMENT_RECEIVED'];
    if (!validReopenOrigins.includes(current)) {
      return {
        valid: false,
        message: 'Projects can only be REOPENED from DELIVERED, BILLED, or PAYMENT_RECEIVED status',
      };
    }
  }

  // If no specific rule was violated, the transition is valid
  return { valid: true };
};

/**
 * Returns filtered and marked status options based on current project status
 * @param allStatuses Array of all possible statuses
 * @param currentStatus Current project status
 * @param isAdmin Whether the user is an admin or super admin (no longer bypasses validation rules)
 * @returns Array of status options with disabled flag for invalid transitions
 */
export const getValidStatusTransitions = (
  allStatuses: string[],
  currentStatus: string | null | undefined,
  isAdmin: boolean = false
): Array<{ value: string; label: string; disabled: boolean; reason?: string }> => {
  return allStatuses.map(status => {
    // For new projects without a status yet, all options are valid
    if (!currentStatus) {
      return {
        value: status,
        label: status,
        disabled: false,
      };
    }

    // Check if this status transition is valid
    const { valid, message } = validateWorkflowTransition(currentStatus, status, isAdmin);

    return {
      value: status,
      label: status,
      disabled: !valid,
      reason: !valid ? message : undefined,
    };
  });
};
